import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // MAL. custom: 未選択時のメイン画像を商品固定にする
    // 機種が選ばれたので、先頭の「商品の1枚目」を取り除いて、選んだ機種の画像を1枚目にする。
    // 差し込む前に行うので、1枚目が一瞬見えることはない。
    this.#malRemoveFixedMedia(newMediaGallery);

    this.replaceWith(newMediaGallery);
  };

  /**
   * MAL. custom: 未選択時のメイン画像を商品固定にする
   *
   * サーバーは常に「商品の1枚目 → 選択中の機種 → その他」の順で描画する
   * （blocks/_product-media-gallery.liquid）。機種が選ばれたので、差し込む前に
   * 先頭の1枚目を取り除いて、選んだ機種の画像を1枚目にする。
   *
   * 取り除きと番号の詰めは window.MALModelChoice.removeGalleryMedia（layout/theme.liquid）が行う。
   * 構造が想定と違うときは何もしない（1枚目が残り、選んだ機種は2枚目になる）。
   *
   * @param {Element} gallery - 差し込む前の新しいギャラリー要素
   */
  #malRemoveFixedMedia(gallery) {
    const modelChoice = /** @type {any} */ (window).MALModelChoice;
    if (!modelChoice) return;

    // 機種が未選択のままなら、商品の1枚目を出したままにする
    if (modelChoice.scopeOf(this)?.hasAttribute('data-mal-unselected')) return;

    modelChoice.removeGalleryMedia(gallery, 0);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
